import { useEffect, useState } from "react";
import "./App.css";

/* ─────────────────────────────────────────────
   SCROLL REVEAL HOOK
───────────────────────────────────────────── */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* ─────────────────────────────────────────────
   NAV
───────────────────────────────────────────── */
function Nav() {
  const [open, setOpen] = useState(false);

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const links = ["About", "Offerings", "AI", "Contact"];

  return (
    <>
      <nav
        data-testid="nav"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "24px 48px",
          background: "rgba(248,247,245,0.92)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        }}>
        {/* Wordmark */}
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 500,
              fontSize: "15px",
              letterSpacing: "0.06em",
              color: "var(--ink-deep)",
              lineHeight: 1,
            }}>
            Digital Billions Group
          </span>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "9px",
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              color: "var(--ink-muted)",
            }}>
            Global Digital Innovation
          </span>
        </div>

        {/* Desktop links */}
        <div
          className="desktop-nav"
          style={{ display: "flex", gap: 36, alignItems: "center" }}>
          {links.map((item) => (
            <a key={item} href={`#${item.toLowerCase()}`} className="nav-link">
              {item}
            </a>
          ))}
        </div>

        {/* Hamburger (mobile) */}
        <button
          className={`hamburger${open ? " open" : ""}`}
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          aria-expanded={open}>
          <span />
          <span />
          <span />
        </button>
      </nav>

      {/* Mobile drawer */}
      <div
        className={`mobile-menu${open ? " open" : ""}`}
        role="dialog"
        aria-label="Navigation menu">
        {links.map((item) => (
          <a
            key={item}
            href={`#${item.toLowerCase()}`}
            className="nav-link"
            onClick={() => setOpen(false)}>
            {item}
          </a>
        ))}
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "9px",
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            color: "var(--ink-faint)",
            marginTop: 20,
          }}>
          ◈ Digital Billions Group
        </span>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   HERO
───────────────────────────────────────────── */
function Hero() {
  return (
    <section
      id="hero"
      data-testid="hero"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        padding: "0 var(--pad-x) 80px",
        position: "relative",
      }}>
      {/* Decorative vertical line */}
      <div
        className="animate-line hero-deco-line"
        style={{
          position: "absolute",
          top: 0,
          left: "var(--pad-x)",
          width: "1px",
          height: "100%",
          background: "var(--border)",
          opacity: 0.5,
        }}
        aria-hidden="true"
      />

      {/* Content */}
      <div style={{ maxWidth: "860px", position: "relative", zIndex: 1 }}>
        <p
          className="section-label animate-hero-0"
          style={{ marginBottom: 40 }}>
          ◈ &nbsp; DIGITAL BILLIONS GROUP &nbsp; ◈ &nbsp; Global Digital
          Innovation
        </p>

        <h1
          className="animate-hero-1"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 300,
            fontSize: "clamp(44px, 8vw, 112px)",
            lineHeight: 0.92,
            letterSpacing: "-0.02em",
            color: "var(--ink-deep)",
            marginBottom: 12,
          }}>
          Innovation,
        </h1>
        <h1
          className="animate-hero-2"
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 300,
            fontStyle: "italic",
            fontSize: "clamp(44px, 8vw, 112px)",
            lineHeight: 0.92,
            letterSpacing: "-0.02em",
            color: "var(--ink-mid)",
            marginBottom: 48,
          }}>
          Elegantly Deployed.
        </h1>

        <p
          className="animate-hero-3"
          style={{
            fontFamily: "var(--font-body)",
            fontWeight: 300,
            fontSize: "16px",
            lineHeight: 1.75,
            color: "var(--ink-soft)",
            maxWidth: "520px",
            marginBottom: 52,
          }}>
          Where intelligent design meets refined technology. Digital Billions
          Group builds the architecture of tomorrow's digital landscape—merging
          AI precision, automated experiences, and creative ecosystems into a
          single harmonious vision. For those who value performance, beauty, and
          innovation in equal measure.
        </p>

        <div className="animate-hero-4">
          <a href="#about" className="btn-white">
            <span>Discover More</span>
            <span aria-hidden="true" style={{ fontSize: "16px" }}>
              ↓
            </span>
          </a>
        </div>
      </div>

      {/* Right-side scroll label */}
      <div className="hero-side-label animate-hero-0" aria-hidden="true">
        <div
          style={{
            writingMode: "vertical-rl",
            fontFamily: "var(--font-mono)",
            fontSize: "9px",
            letterSpacing: "0.3em",
            textTransform: "uppercase",
            color: "var(--ink-faint)",
          }}>
          Scroll to explore
        </div>
        <div
          style={{ width: "1px", height: "60px", background: "var(--border)" }}
        />
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   ABOUT
───────────────────────────────────────────── */
function About() {
  return (
    <section
      id="about"
      data-testid="about"
      className="section-pad"
      style={{ background: "var(--bg-alt)" }}>
      <div className="two-col">
        {/* Left */}
        <div className="reveal">
          <p className="section-label" style={{ marginBottom: 32 }}>
            § &nbsp; About Us
          </p>
          <div
            style={{
              width: "40px",
              height: "1px",
              background: "var(--ink-muted)",
              marginBottom: 32,
            }}
          />
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 300,
              fontSize: "clamp(32px, 4vw, 52px)",
              lineHeight: 1.08,
              letterSpacing: "-0.01em",
              color: "var(--ink-deep)",
            }}>
            Crafting the Future
            <br />
            <em style={{ color: "var(--ink-mid)" }}>in Digital Form.</em>
          </h2>
        </div>

        {/* Right */}
        <div className="reveal" style={{ paddingTop: 4 }}>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 300,
              fontSize: "17px",
              lineHeight: 1.85,
              color: "var(--ink-soft)",
              marginBottom: 28,
            }}>
            Digital Billions Group is a global innovator in digital services and
            technologies—designed for forward-thinking brands, creators, and
            enterprises.
          </p>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 300,
              fontSize: "17px",
              lineHeight: 1.85,
              color: "var(--ink-soft)",
              marginBottom: 28,
            }}>
            We specialize in seamlessly blending the human experience with
            algorithmic intelligence. Each venture under our name operates with
            a dedication to clarity, customization, and craft. Whether
            developing next‑generation vending experiences or curating digital
            art and media streams, we believe technology should not just serve
            society—but elevate it.
          </p>
          <p
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: "20px",
              lineHeight: 1.6,
              color: "var(--ink-mid)",
              borderLeft: "2px solid var(--border)",
              paddingLeft: "20px",
              marginTop: 40,
            }}>
            Refine everything. Automate intelligently. Create with purpose.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   OFFERINGS
───────────────────────────────────────────── */
const offerings = [
  {
    number: "01",
    glyph: "◎",
    title: "Agentic AI Workflows",
    description:
      "Our bespoke agentic systems automate intelligently, learning and adapting to your needs. We design tailored AI agents to simplify operations, enhance productivity, and deliver customer engagement that feels almost human in its intuition.",
  },
  {
    number: "02",
    glyph: "◫",
    title: "Digital Vending",
    description:
      "Elevate convenience through innovation. From luxury snack and beverage systems to photo, toy, apparel, and themed entertainment machines, our vending technology fuses aesthetics with strategy to make every transaction a moment of experience.",
  },
  {
    number: "03",
    glyph: "◈",
    title: "Multi‑Media Publishing",
    description:
      "We partner with artists, creators, and performance brands to amplify their digital presence. Our global publishing network bridges creation and distribution with professionalism, precision, and style.",
  },
  {
    number: "04",
    glyph: "◉",
    title: "Digital Marketing",
    description:
      "Select clients receive white‑glove digital marketing solutions that blend analytics with artistry. We specialize in Meta, TikTok, and Google advertising, alongside bespoke SEO and email campaigns that convert influence into measurable growth.",
  },
];

function Offerings() {
  return (
    <section id="offerings" data-testid="offerings" className="section-pad">
      <div className="offerings-header reveal">
        <div>
          <p className="section-label" style={{ marginBottom: 20 }}>
            ◈ &nbsp; What We Do
          </p>
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 300,
              fontSize: "clamp(32px, 4vw, 56px)",
              lineHeight: 1,
              letterSpacing: "-0.01em",
              color: "var(--ink-deep)",
            }}>
            Our Offerings
          </h2>
        </div>
        <p
          className="sub-label"
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "10px",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            color: "var(--ink-muted)",
            maxWidth: "220px",
            textAlign: "right",
            lineHeight: 1.7,
          }}>
          Four pillars of digital excellence
        </p>
      </div>

      <div>
        {offerings.map((o, i) => (
          <div
            key={o.number}
            className="offering-card reveal"
            style={{ transitionDelay: `${i * 0.08}s` }}>
            <div className="offering-inner">
              {/* Number + glyph */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "10px",
                    letterSpacing: "0.2em",
                    color: "var(--ink-faint)",
                    textTransform: "uppercase",
                  }}>
                  {o.number}
                </span>
                <span
                  style={{
                    fontSize: "20px",
                    color: "var(--ink-muted)",
                    lineHeight: 1,
                  }}>
                  {o.glyph}
                </span>
              </div>
              {/* Title */}
              <h3
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 400,
                  fontSize: "clamp(20px, 2.5vw, 30px)",
                  lineHeight: 1.15,
                  letterSpacing: "-0.01em",
                  color: "var(--ink-deep)",
                  paddingTop: 4,
                }}>
                {o.title}
              </h3>
              {/* Description */}
              <p
                className="offering-desc"
                style={{
                  fontFamily: "var(--font-body)",
                  fontWeight: 300,
                  fontSize: "15px",
                  lineHeight: 1.8,
                  color: "var(--ink-soft)",
                }}>
                {o.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   AI SERVICES
───────────────────────────────────────────── */
const pricingPlans = [
  {
    tier: "01",
    glyph: "◎",
    name: "AI Strategy Call",
    price: "$99",
    cadence: "One Time",
    tag: "Start here",
    featured: false,
    description:
      "A 30-minute session with our automation team. Perfect for business owners who want to explore automation before committing to a full system.",
    includes: [
      "Review of your current processes",
      "Identify tasks that could be automated",
      "Recommend the right AI assistant or workflow",
      "Outline a simple implementation plan",
    ],
    cta: "Book My Strategy Call",
    ctaSymbol: "→",
  },
  {
    tier: "02",
    glyph: "◉",
    name: "Starter Assistant",
    price: "$299",
    cadence: "/ month",
    tag: "Most popular",
    featured: true,
    description:
      "Perfect for small businesses that want a basic AI assistant running 24/7 — handling inquiries, bookings, and lead capture automatically.",
    includes: [
      "AI website chat assistant",
      "Automated appointment booking",
      "Lead capture and notifications",
      "Basic workflow automation",
      "Monthly system monitoring",
    ],
    cta: "Start My AI Assistant",
    ctaSymbol: "→",
  },
  {
    tier: "03",
    glyph: "◈",
    name: "Business Automation",
    price: "$799",
    cadence: "/ month",
    tag: "Full stack",
    featured: false,
    description:
      "Ideal for companies that want AI handling customer communication, scheduling, and CRM updates end-to-end.",
    includes: [
      "AI website & messaging assistant",
      "Automated booking system",
      "Email and SMS follow-ups",
      "CRM integration",
      "Lead qualification workflows",
      "Monthly optimization and updates",
    ],
    cta: "Launch My Automation System",
    ctaSymbol: "→",
  },
  {
    tier: "04",
    glyph: "◫",
    name: "Advanced Workflow Systems",
    price: "From $2,500",
    cadence: "/ month",
    tag: "Enterprise",
    featured: false,
    description:
      "For companies ready to implement larger automation systems across departments — fully customized to your business processes.",
    includes: [
      "AI customer service teams",
      "Sales and lead management automation",
      "Operations workflow automation",
      "Internal AI assistants for staff",
      "Data processing and reporting agents",
    ],
    cta: "Schedule Enterprise Consultation",
    ctaSymbol: "↗",
  },
];

function PricingCard({ plan }) {
  const dark = plan.featured;
  const fg = dark ? "#F8F7F5" : "var(--ink-deep)";
  const fgSub = dark ? "rgba(248,247,245,0.55)" : "var(--ink-soft)";
  const fgMut = dark ? "rgba(248,247,245,0.35)" : "var(--ink-faint)";
  const divider = dark ? "rgba(248,247,245,0.1)" : "var(--border)";

  return (
    <div
      style={{
        background: dark ? "var(--ink-deep)" : "var(--bg)",
        padding: "40px 32px 36px",
        display: "flex",
        flexDirection: "column",
      }}>
      {/* Top meta */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 28,
        }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "9px",
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            color: fgMut,
          }}>
          {plan.tier}
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "9px",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: dark ? "rgba(248,247,245,0.4)" : "var(--ink-muted)",
            border: dark
              ? "1px solid rgba(248,247,245,0.2)"
              : "1px solid var(--border)",
            padding: "4px 8px",
          }}>
          {plan.tag}
        </span>
      </div>

      <div style={{ fontSize: "22px", color: fgMut, marginBottom: 16 }}>
        {plan.glyph}
      </div>

      <h3
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 400,
          fontSize: "22px",
          lineHeight: 1.15,
          letterSpacing: "-0.01em",
          color: fg,
          marginBottom: 12,
        }}>
        {plan.name}
      </h3>

      <div style={{ marginBottom: 20 }}>
        <span
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 300,
            fontStyle: "italic",
            fontSize: "32px",
            color: fg,
            letterSpacing: "-0.02em",
          }}>
          {plan.price}
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "10px",
            letterSpacing: "0.15em",
            color: dark ? "rgba(248,247,245,0.4)" : "var(--ink-muted)",
            marginLeft: 6,
            textTransform: "uppercase",
          }}>
          {plan.cadence}
        </span>
      </div>

      <div style={{ height: "1px", background: divider, marginBottom: 20 }} />

      <p
        style={{
          fontFamily: "var(--font-body)",
          fontWeight: 300,
          fontSize: "13px",
          lineHeight: 1.75,
          color: fgSub,
          marginBottom: 24,
          flexGrow: 1,
        }}>
        {plan.description}
      </p>

      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: "0 0 32px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}>
        {plan.includes.map((item, i) => (
          <li
            key={i}
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 300,
              fontSize: "12px",
              lineHeight: 1.6,
              color: dark ? "rgba(248,247,245,0.5)" : "var(--ink-soft)",
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
            }}>
            <span
              style={{
                color: fgMut,
                flexShrink: 0,
                marginTop: 1,
                fontSize: "10px",
              }}>
              ◌
            </span>
            {item}
          </li>
        ))}
      </ul>

      <a
        href="mailto:info@digitalbillionsgroup.com"
        className={dark ? "btn-black" : "btn-white"}
        style={
          dark
            ? {
                background: "#F8F7F5",
                color: "var(--ink-deep)",
                border: "none",
                justifyContent: "space-between",
              }
            : { justifyContent: "space-between" }
        }>
        <span style={{ fontSize: "11px" }}>{plan.cta}</span>
        <span aria-hidden="true">{plan.ctaSymbol}</span>
      </a>
    </div>
  );
}

function AIServices() {
  return (
    <section
      id="ai"
      data-testid="ai-services"
      className="section-pad"
      style={{ background: "var(--bg-alt)" }}>
      {/* Header */}
      <div className="ai-header reveal">
        <div>
          <p className="section-label" style={{ marginBottom: 20 }}>
            ◉ &nbsp; AI & Automation
          </p>
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 300,
              fontSize: "clamp(32px, 4vw, 56px)",
              lineHeight: 1.0,
              letterSpacing: "-0.01em",
              color: "var(--ink-deep)",
            }}>
            AI Assistants &<br />
            <em style={{ color: "var(--ink-mid)" }}>Automated Workflows</em>
          </h2>
        </div>
        <div>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 300,
              fontSize: "17px",
              lineHeight: 1.85,
              color: "var(--ink-soft)",
              marginBottom: 24,
            }}>
            Digital Billions Group builds custom AI assistants and automated
            workflows that handle everyday business tasks — so your team can
            focus on growth. Our systems answer customer questions, schedule
            appointments, follow up with leads, and manage routine processes
            automatically.
          </p>
          <p
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: "18px",
              color: "var(--ink-mid)",
              borderLeft: "2px solid var(--border)",
              paddingLeft: "20px",
            }}>
            Think of it as a digital assistant working for your business 24/7.
          </p>
        </div>
      </div>

      {/* Pricing grid */}
      <div className="pricing-grid reveal">
        {pricingPlans.map((plan) => (
          <PricingCard key={plan.tier} plan={plan} />
        ))}
      </div>

      {/* CTA band */}
      <div className="ai-cta-band reveal">
        <div>
          <p className="section-label" style={{ marginBottom: 16 }}>
            ◈ &nbsp; Ready to Add AI to Your Business?
          </p>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 300,
              fontSize: "15px",
              lineHeight: 1.75,
              color: "var(--ink-soft)",
              maxWidth: "480px",
            }}>
            Whether you want a simple booking assistant or a fully automated
            workflow system, Digital Billions Group can design the right
            solution. Start with a quick strategy session or launch your
            assistant today.
          </p>
        </div>
        <div className="ai-cta-band-buttons">
          <a href="mailto:info@digitalbillionsgroup.com" className="btn-black">
            <span>Get Started With AI</span>
            <span aria-hidden="true">→</span>
          </a>
          <a href="mailto:info@digitalbillionsgroup.com" className="btn-white">
            <span>Book My $99 Strategy Call</span>
            <span aria-hidden="true">◎</span>
          </a>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   CONTACT
───────────────────────────────────────────── */
function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact"
      className="section-pad"
      style={{ background: "var(--ink-deep)" }}>
      <div className="contact-grid">
        {/* Left */}
        <div className="reveal">
          <p
            className="section-label"
            style={{ marginBottom: 32, color: "rgba(255,255,255,0.3)" }}>
            § &nbsp; Get in Touch
          </p>
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 300,
              fontSize: "clamp(36px, 5vw, 68px)",
              lineHeight: 1.0,
              letterSpacing: "-0.02em",
              color: "#F8F7F5",
              marginBottom: 32,
            }}>
            Let's Create
            <br />
            <em style={{ color: "rgba(248,247,245,0.6)" }}>What's Next.</em>
          </h2>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 300,
              fontSize: "16px",
              lineHeight: 1.8,
              color: "rgba(248,247,245,0.55)",
              marginBottom: 52,
              maxWidth: "380px",
            }}>
            For partnership inquiries, collaborations, and customized solutions,
            reach our executive team below. Your next chapter in digital
            evolution starts here.
          </p>
          <a
            href="mailto:info@digitalbillionsgroup.com"
            className="btn-white"
            style={{
              background: "transparent",
              color: "#F8F7F5",
              borderColor: "rgba(248,247,245,0.35)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#F8F7F5";
              e.currentTarget.style.color = "var(--ink-deep)";
              e.currentTarget.style.borderColor = "#F8F7F5";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "#F8F7F5";
              e.currentTarget.style.borderColor = "rgba(248,247,245,0.35)";
            }}>
            <span>Contact Us</span>
            <span aria-hidden="true">→</span>
          </a>
        </div>

        {/* Right */}
        <div
          className="reveal contact-right"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 48,
            alignItems: "flex-end",
          }}>
          <div style={{ textAlign: "right" }}>
            <p
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "9px",
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: "rgba(248,247,245,0.3)",
                marginBottom: 10,
              }}>
              ✉ &nbsp; Executive Inquiries
            </p>
            <a
              href="mailto:info@digitalbillionsgroup.com"
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 300,
                fontSize: "clamp(15px, 2vw, 22px)",
                color: "rgba(248,247,245,0.8)",
                textDecoration: "none",
                letterSpacing: "0.01em",
                transition: "color 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#F8F7F5")}
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = "rgba(248,247,245,0.8)")
              }>
              info@digitalbillionsgroup.com
            </a>
          </div>
          <div
            style={{
              textAlign: "right",
              borderTop: "1px solid rgba(248,247,245,0.1)",
              paddingTop: 32,
              width: "100%",
            }}>
            <p
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "9px",
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: "rgba(248,247,245,0.2)",
                lineHeight: 2,
              }}>
              Digital Billions Group
              <br />
              Worldwide · Online · Always Connected.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   FOOTER
───────────────────────────────────────────── */
function Footer() {
  return (
    <footer
      data-testid="footer"
      className="site-footer"
      style={{
        background: "var(--ink-deep)",
        borderTop: "1px solid rgba(248,247,245,0.07)",
      }}>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "9px",
          letterSpacing: "0.25em",
          textTransform: "uppercase",
          color: "rgba(248,247,245,0.2)",
        }}>
        © {new Date().getFullYear()} Digital Billions Group
      </span>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "9px",
          letterSpacing: "0.25em",
          textTransform: "uppercase",
          color: "rgba(248,247,245,0.2)",
        }}>
        ◈ All Rights Reserved
      </span>
    </footer>
  );
}

/* ─────────────────────────────────────────────
   APP
───────────────────────────────────────────── */
function App() {
  useScrollReveal();
  return (
    <>
      <div className="noise" aria-hidden="true" />
      <Nav />
      <main data-testid="main-wrapper">
        <Hero />
        <About />
        <Offerings />
        <AIServices />
        <Contact />
      </main>
      <Footer />
    </>
  );
}

export default App;
